import React from 'react';
import { Input } from './ui/input';
import { Check, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

// Validation functions
export const validators = {
  // Email validation - checks for valid email format
  email: (value) => {
    if (!value) return { valid: false, empty: true };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return { valid: emailRegex.test(value), empty: false };
  },

  // Phone validation - checks for 10 digits (formatted as (xxx) xxx-xxxx)
  phone: (value) => {
    if (!value) return { valid: false, empty: true };
    const digits = value.replace(/\D/g, '');
    return { valid: digits.length === 10, empty: false };
  },

  // SIN validation - checks for 9 digits (formatted as xxx-xxx-xxx)
  sin: (value) => {
    if (!value) return { valid: false, empty: true };
    const digits = value.replace(/\D/g, '');
    return { valid: digits.length === 9, empty: false };
  },

  // Name validation - checks for at least 2 characters
  name: (value) => {
    if (!value) return { valid: false, empty: true };
    return { valid: value.trim().length >= 2, empty: false };
  },

  // Required field - just checks if not empty
  required: (value) => {
    if (!value) return { valid: false, empty: true };
    return { valid: value.trim().length > 0, empty: false };
  }
};

// ValidatedInput component with visual validation indicators
const ValidatedInput = React.forwardRef(({ 
  className,
  validationType, // 'email' | 'phone' | 'sin' | 'name' | 'required'
  value,
  showValidation = true, // Set to false to hide indicators
  required = false,
  ...props 
}, ref) => {
  // Get validation result
  const getValidation = () => {
    if (!validationType || !showValidation) return null;
    const validator = validators[validationType];
    if (!validator) return null;
    return validator(value);
  };

  const validation = getValidation();
  
  // Determine visual state
  const showIndicator = validation && !validation.empty;
  const isValid = validation?.valid;
  
  // Border color classes
  const borderClass = showIndicator 
    ? isValid 
      ? 'border-green-500 focus-visible:ring-green-500' 
      : 'border-red-400 focus-visible:ring-red-400'
    : '';

  return (
    <div className="relative">
      <Input
        ref={ref}
        value={value}
        className={cn(
          borderClass,
          showIndicator ? 'pr-9' : '',
          className
        )}
        {...props}
      />
      {showIndicator && (
        <div className={cn(
          "absolute right-3 top-1/2 -translate-y-1/2",
          isValid ? "text-green-500" : "text-red-400"
        )}>
          {isValid ? (
            <Check className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
        </div>
      )}
    </div>
  );
});

ValidatedInput.displayName = 'ValidatedInput';

export default ValidatedInput;
